import { useEffect, useRef, useState } from "react";

import { getEncodingProfile } from "./encodingProfiles";
import { useUiLanguage } from "./uiLanguageContext";
import { knownNumberWikipediaCacheKey } from "./knownNumberWikipedia";
import {
  knownNumberAtPointer,
  numberLineIndexAtPointer,
} from "./knownNumbers";
import {
  shouldBlockNumberRowInsertion,
} from "./lib/nummem";
import type {
  EncodingSystem,
  KnownNumberEntry,
  KnownNumberPart,
  WikipediaSummary,
} from "./types";

const PREVIEW_CLOSE_DELAY_MS = 1600;

type KnownNumberInputProps = {
  activeHoverWindow: string | null;
  entries: KnownNumberEntry[];
  encodingSystem: EncodingSystem;
  hoverWindowId: string;
  maximumRows: number;
  onChange: (value: string) => void;
  onClear: () => void;
  onHoverWindowClose: (windowId: string) => void;
  onHoverWindowOpen: (windowId: string) => void;
  onHoveredLineChange: (lineIndex: number | null) => void;
  onHeightChange: (height: number) => void;
  onRowLimitReached: () => void;
  onWikipediaPreview: (entry: KnownNumberEntry) => void;
  onStripNonNumericChange: (enabled: boolean) => void;
  stripNonNumeric: boolean;
  summaries: Record<string, WikipediaSummary | null | undefined>;
  value: string;
  wikipediaPreviewsEnabled: boolean;
};

type HoveredNumber = {
  above: boolean;
  centered: boolean;
  left: number;
  maxHeight: number;
  entry: KnownNumberEntry;
  part: KnownNumberPart;
  top: number;
  width?: number;
};

export function KnownNumberInput({
  activeHoverWindow,
  entries,
  encodingSystem,
  hoverWindowId,
  maximumRows,
  onChange,
  onClear,
  onHoverWindowClose,
  onHoverWindowOpen,
  onHoveredLineChange,
  onHeightChange,
  onRowLimitReached,
  onWikipediaPreview,
  onStripNonNumericChange,
  summaries,
  value,
  stripNonNumeric,
  wikipediaPreviewsEnabled,
}: KnownNumberInputProps) {
  const { text: t } = useUiLanguage();
  const [hovered, setHovered] = useState<HoveredNumber | null>(null);
  const [hoveredLine, setHoveredLine] = useState<number | null>(null);
  const [closeButtonVisible, setCloseButtonVisible] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const previewKey = useRef("");
  const visiblePreviewKey = useRef("");
  const closeTimer = useRef<number | undefined>(undefined);
  const closeButtonTimer = useRef<number | undefined>(undefined);
  const textarea = useRef<HTMLTextAreaElement | null>(null);
  const encodingProfile = getEncodingProfile(encodingSystem);
  const activeEntry = hovered?.entry;
  const summary = activeEntry
    ? summaries[knownNumberWikipediaCacheKey(encodingSystem, activeEntry)]
    : undefined;
  const activeSummary = wikipediaPreviewsEnabled ? summary : undefined;
  const previewHref = wikipediaPreviewsEnabled
    ? activeSummary?.content_urls?.desktop?.page ??
      (activeEntry && encodingProfile.usesCatalogKnownNumberMetadata
        ? activeEntry.evidence_url
        : undefined)
    : undefined;
  const lines = value.split(/\r?\n/);

  useEffect(
    () => () => {
      window.clearTimeout(closeTimer.current);
      window.clearTimeout(closeButtonTimer.current);
    },
    [],
  );

  useEffect(() => {
    const input = textarea.current;
    if (!input) {
      return;
    }
    const updateHeight = () => onHeightChange(input.getBoundingClientRect().height);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(input);
    return () => observer.disconnect();
  }, [onHeightChange]);

  function clearPreview() {
    window.clearTimeout(closeTimer.current);
    window.clearTimeout(closeButtonTimer.current);
    previewKey.current = "";
    visiblePreviewKey.current = "";
    setCloseButtonVisible(false);
    setHovered(null);
    onHoverWindowClose(hoverWindowId);
  }

  function startCloseButtonDelay(key: string) {
    if (visiblePreviewKey.current === key) {
      return;
    }
    visiblePreviewKey.current = key;
    window.clearTimeout(closeButtonTimer.current);
    setCloseButtonVisible(false);
    closeButtonTimer.current = window.setTimeout(
      () => setCloseButtonVisible(true),
      2000,
    );
  }

  function keepPreviewOpen() {
    window.clearTimeout(closeTimer.current);
  }

  function schedulePreviewClose() {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(
      clearPreview,
      PREVIEW_CLOSE_DELAY_MS,
    );
  }

  function showPreviewAtPosition(
    textarea: HTMLTextAreaElement,
    clientX: number,
    clientY: number,
    centered = false,
  ) {
    const bounds = textarea.getBoundingClientRect();
    const style = getComputedStyle(textarea);
    const fontSize = Number.parseFloat(style.fontSize) || 16;
    const lineHeight = Number.parseFloat(style.lineHeight) || fontSize * 1.75;
    const metrics = {
      pointerY: clientY - bounds.top,
      paddingTop: Number.parseFloat(style.paddingTop) || 0,
      lineHeight,
      scrollTop: textarea.scrollTop,
    };
    const lineIndex = numberLineIndexAtPointer(value, metrics);
    setHoveredLine(lineIndex);
    onHoveredLineChange(lineIndex);
    const match = knownNumberAtPointer(value, entries, metrics);
    if (!match) {
      clearPreview();
      return;
    }
    const { entry, part } = match;
    const key = `${encodingSystem}:${entry.id}:${part.digits}`;
    onHoverWindowOpen(hoverWindowId);
    startCloseButtonDelay(key);
    if (wikipediaPreviewsEnabled && previewKey.current !== key) {
      previewKey.current = key;
      onWikipediaPreview(entry);
    }
    if (centered) {
      const horizontalHalfSpace = Math.max(
        1,
        Math.min(clientX - 12, window.innerWidth - clientX - 12),
      );
      const verticalHalfSpace = Math.max(
        1,
        Math.min(clientY - 12, window.innerHeight - clientY - 12),
      );
      setHovered({
        above: false,
        centered: true,
        entry,
        left: clientX,
        maxHeight: Math.min(window.innerHeight - 24, verticalHalfSpace * 2),
        part,
        top: clientY,
        width: Math.min(420, window.innerWidth - 32, horizontalHalfSpace * 2),
      });
      return;
    }
    const above = clientY > window.innerHeight / 2;
    const cardWidth = Math.min(420, window.innerWidth - 24);
    setHovered({
      above,
      centered: false,
      entry,
      left: Math.max(
        12,
        Math.min(clientX + 16, window.innerWidth - cardWidth - 12),
      ),
      maxHeight: Math.max(
        180,
        above ? clientY - 16 : window.innerHeight - clientY - 16,
      ),
      part,
      top: above ? clientY - 14 : clientY + 14,
    });
  }

  return (
    <div className="number-panel">
      <div className="number-panel-header">
        <label className="panel-title" htmlFor="numbers-to-encode">{t("numbers")}</label>
        <span className="number-action-shell">
          <button
            className="number-clear-button"
            type="button"
            aria-label={t("clearNumberInput")}
            onClick={onClear}
          >
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M6 6 18 18M18 6 6 18" />
            </svg>
          </button>
          <span className="number-action-tooltip" role="tooltip" aria-label={t("clearInput")}>
            {t("clearInput")}
          </span>
        </span>
        <span className="number-action-shell">
          <label className="input-filter-switch">
            <input
              type="checkbox"
              role="switch"
              aria-label={t("nonNumericFilterLabel")}
              checked={stripNonNumeric}
              onChange={(event) =>
                onStripNonNumericChange(event.target.checked)
              }
            />
            <span className="input-filter-track" aria-hidden="true">
              <span className="input-filter-thumb">
                <svg viewBox="0 0 22 22">
                  <text x="1" y="16.5">A!</text>
                  <path d="M4 17 18 5" />
                </svg>
              </span>
            </span>
          </label>
          <span className="number-action-tooltip" role="tooltip" aria-label={t("nonNumericFilter")}>
            {t("nonNumericFilter")}
          </span>
        </span>
      </div>
      <span
        className="number-input-shell"
        onMouseEnter={keepPreviewOpen}
        onMouseLeave={() => {
          setHoveredLine(null);
          onHoveredLineChange(null);
          schedulePreviewClose();
        }}
      >
        <textarea
          ref={textarea}
          id="numbers-to-encode"
          aria-label={t("numbersToEncode")}
          spellCheck={false}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              shouldBlockNumberRowInsertion(
                value,
                event.currentTarget.selectionStart,
                event.currentTarget.selectionEnd,
                maximumRows,
              )
            ) {
              event.preventDefault();
              onRowLimitReached();
            }
          }}
          onScroll={(event) => {
            setScrollTop(event.currentTarget.scrollTop);
            clearPreview();
          }}
          onMouseMove={(event) =>
            showPreviewAtPosition(
              event.currentTarget,
              event.clientX,
              event.clientY,
            )
          }
          onClick={(event) =>
            showPreviewAtPosition(
              event.currentTarget,
              event.clientX,
              event.clientY,
              true,
            )
          }
        />
        <span className="number-row-overlay">
          <span
            className="number-row-overlay-content"
            style={{ transform: `translateY(${-scrollTop}px)` }}
          >
            {lines.map((line, index) => (
              <span className="number-overlay-row" key={`${line}-${index}`}>
                <span
                  className={`number-overlay-text${hoveredLine === index ? " number-overlay-text-active" : ""}`}
                  aria-hidden="true"
                >
                  {line || " "}
                </span>
              </span>
            ))}
          </span>
        </span>
        {hovered && activeHoverWindow === hoverWindowId ? (
          <span
            className={`known-number-preview${hovered.above ? " known-number-preview-above" : ""}${hovered.centered ? " known-number-preview-pointer-centered" : ""}`}
            role="tooltip"
            style={{
              left: hovered.left,
              maxHeight: hovered.maxHeight,
              top: hovered.top,
              width: hovered.width,
            }}
            onMouseEnter={keepPreviewOpen}
            onMouseLeave={schedulePreviewClose}
          >
            {closeButtonVisible ? (
              <button
                className="preview-close-button"
                type="button"
                aria-label={t("closePreview")}
                onClick={clearPreview}
              >
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M6 6l12 12M18 6 6 18" />
                </svg>
              </button>
            ) : null}
            {activeSummary?.thumbnail?.source ? (
              <img src={activeSummary.thumbnail.source} alt="" />
            ) : null}
            <strong>
              {encodingProfile.usesCatalogKnownNumberMetadata
                ? hovered.entry.label
                : activeSummary?.title ?? `${t(encodingSystem)} ${t("wikipedia")}`}
            </strong>
            {encodingProfile.usesCatalogKnownNumberMetadata ? (
              <>
                <span className="known-number-metadata-label">
                  {t("overallRepresentation")}
                </span>
                <span className="known-number-representation">
                  {hovered.entry.representation}
                </span>
                <span className="known-number-metadata-label">{t("hoveredPart")}</span>
                <span className="known-number-part">
                  {hovered.part.display} - {hovered.part.meaning}
                </span>
              </>
            ) : (
              <>
                <span className="known-number-metadata-label">{t("number")}</span>
                <span className="known-number-part">{hovered.part.digits}</span>
              </>
            )}
            {wikipediaPreviewsEnabled && summary === undefined ? (
              <span className="preview-muted">{t("loadingWikipediaPreview")}</span>
            ) : null}
            {wikipediaPreviewsEnabled && summary === null ? (
              <span className="preview-muted">{t("noExactWikipediaPeriod")}</span>
            ) : null}
            {activeSummary ? <span>{activeSummary.extract}</span> : null}
            {previewHref ? (
              <a href={previewHref} target="_blank" rel="noreferrer">
                {activeSummary || !encodingProfile.usesCatalogKnownNumberMetadata
                  ? t("openWikipediaPage")
                  : t("openSource")}
              </a>
            ) : null}
          </span>
        ) : null}
      </span>
    </div>
  );
}
